import { useState, FormEvent, ChangeEvent, useMemo } from "react";
import { useDispatch } from "react-redux";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { SelectOption } from "@idw-types/common";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { AttributeTypeSelectFormGroup } from "../../common/select-form-groups";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";
import { AttributeLabel, AttributeType } from "@idw-types/data/reference-data/generic";
import { createAxnAttributeLabelRequest } from "@requests/management-console/create-axn-attribute-label";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";

type AddRpServiceConfigurationAttributeLabelModalProps = {
    onModalClose: () => void;
    attributeLabels: Array<AttributeLabel>;
    onAttributeLabelCreate: (attributeLabel: AttributeLabel) => void;
    attributeType: AttributeType;
};

type AddRpServiceConfigurationAttributeLabelModalState = {
    requestInProgress: boolean;
    attributeLabel: AttributeLabel;
    selectedAttributeTypeOption: SelectOption;
};

export const AddRpServiceConfigurationAttributeLabelModal = (props: AddRpServiceConfigurationAttributeLabelModalProps) => {
    const dispatch = useDispatch();
    const { onModalClose, onAttributeLabelCreate, attributeType, attributeLabels } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [state, setState] = useState<AddRpServiceConfigurationAttributeLabelModalState>({
        requestInProgress: false,
        attributeLabel: {
            attributeTypeID: attributeType.id,
            id: "",
            label: ""
        },
        selectedAttributeTypeOption: null
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setState({ ...state, requestInProgress: true });

        const { data, error, body } = await createAxnAttributeLabelRequest(state.attributeLabel);
        if (data?.response) {
            onAttributeLabelCreate(data.response);
            onModalClose();
        } else {
            if (error) {
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.requests.createAttributeLabel.error.title,
                    resources.requests.createAttributeLabel.error.message,
                    null,
                    null,
                    body    
                );
            }
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleUpdateLabel = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            attributeLabel: {
                ...state.attributeLabel,
                label: event.target.value
            }
        });
    };

    const isAttributeLabelValid = useMemo(()=> {
        const { label } = state.attributeLabel;
        return attributeLabels.every(attributeLabel => attributeLabel.label.toLowerCase() !== label.toLowerCase());
    }, [attributeLabels, state.attributeLabel.label])

    return (
        <DefaultModal
            automationId="add-rp-service-configuration-attribute-label-modal"
            ariaLabelledBy="add-rp-service-configuration-attribute-label-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-rp-service-configuration-attribute-label-title"} closeButton>
                    <Modal.Title>{resources.modals.addAttributeLabel.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputFormGroup
                        controlId="rp-service-configuration-attribute-label-label-input"
                        label={resources.common.inputs.attributeLabel.label}
                        ariaLabel={resources.common.inputs.attributeLabel.label}
                        automationId="rp-service-configuration-attribute-label-label-input"
                        placeholder={resources.common.inputs.attributeLabel.placeholder}
                        value={state.attributeLabel.label}
                        onChange={handleUpdateLabel}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />
                    {!isAttributeLabelValid && <Alert variant="danger" >{"This attribute label already exists ."}</Alert>}
                    <AttributeTypeSelectFormGroup
                        idPrefix="management-console"
                        onChange={() => {}}
                        axnAttributeTypes={[attributeType]}
                        selectedOption={{ label: attributeType.name, value: attributeType.id }}
                        disabled={true}
                        isRequired={true}
                        isSearchable={false}
                        isClearable={false}
                        labelWidth={labelWidth}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        disabled={!isAttributeLabelValid || state.requestInProgress}
                        hideSpinner={false}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={resources.common.buttons.save.label}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};